// HoldingsTabs — the Holdings card below the action strip: a 4-tab breakdown of
// the plan's holdings (By Fund / By Asset Class / By Source / Performance) on one
// library Surface. Owns only the active-tab state; each panel is its own pure
// sub-component fed plain props. Source of truth: portfolio3.jsx .pf3-holdings.
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Surface, Tabs, Skeleton, AsOfPill } from '../../../reusable';
import { formatDate, localDate } from '../../../../utils/format';
import { ByFundTab } from './ByFundTab';
import { ByAssetClassTab } from './ByAssetClassTab';
import { BySourceTab } from './BySourceTab';
import { PerformanceTab, hasReturns } from './PerformanceTab';
import './holdings-tabs.css';

// Tab ids are stable (drive the active-panel switch); labels resolve via i18n.
const TAB_KEYS = [
  { id: 'fund', labelKey: 'fund', shortKey: 'fundShort' },
  { id: 'asset', labelKey: 'asset', shortKey: 'assetShort' },
  { id: 'source', labelKey: 'source', shortKey: 'sourceShort' },
  { id: 'perf', labelKey: 'performance', shortKey: 'performanceShort' },
];

export function HoldingsTabs({
  funds = [],
  tree,
  summary,
  holdings,
  assetClasses,
  sources,
  classSpark,
  asOf,
  plan,
  onPickFund,
  onPickModel,
  loading = false,
  // Optional card heading, rendered above the tab strip with the as-of date beside it.
  // The combined dashboard (FM-2406) passes "Holdings", because this card replaced the
  // retired HoldingsGlimpse and has to keep announcing what the section IS. The
  // Portfolio page omits it — there the page breadcrumb already says "My Portfolio",
  // so a second heading would be noise. Caller-supplied so this module stays free of
  // another module's translation keys.
  title,
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('fund');
  const tabDefs = TAB_KEYS.map((d) => ({
    id: d.id,
    label: t(`portfolio.tabs.${d.labelKey}`),
    short: t(`portfolio.tabs.${d.shortKey}`),
  }));
  // Returns carry their own per-fund date, usually a quarter-end behind the balance (FM-3094).
  const latestPerf = funds.reduce((d, f) => (hasReturns(f) && f.perfAsOf > d ? f.perfAsOf : d), '');
  const returnsAsOf = latestPerf ? localDate(latestPerf) : asOf;

  if (loading) {
    return (
      <Surface className="holdings-tabs" padding="none">
        <div className="holdings-tabs__skeleton" aria-busy="true">
          <Skeleton variant="text" width={280} />
          <Skeleton variant="block" height={320} />
        </div>
      </Surface>
    );
  }

  return (
    <Surface className="holdings-tabs" padding="none">
      {/* Card heading. The as-of pill sits BESIDE the title because the date qualifies
          the heading ("Holdings, as of Jun 3") — the same reasoning the retired
          HoldingsGlimpse used. The tab strip's own "Returns as of …" caption is a
          separate claim about the RETURN figures, so it stays where it is. */}
      {title && (
        <div className="holdings-tabs__title-row">
          <h2 className="holdings-tabs__title">{title}</h2>
          {asOf && <AsOfPill date={asOf} />}
        </div>
      )}
      <header className="holdings-tabs__head">
        <Tabs tabs={tabDefs} value={tab} onChange={setTab} className="holdings-tabs__tabs" />
        {returnsAsOf && (
          <div className="holdings-tabs__meta">
            {t('portfolio.tabs.returnsAsOf', { date: formatDate(returnsAsOf) })}
          </div>
        )}
      </header>
      <div className="holdings-tabs__body">
        {tab === 'fund' && (
          <ByFundTab
            funds={funds}
            tree={tree}
            summary={summary}
            holdings={holdings}
            assetClasses={assetClasses}
            plan={plan}
            onPickFund={onPickFund}
            onPickModel={onPickModel}
          />
        )}
        {tab === 'asset' && (
          <ByAssetClassTab assetClasses={assetClasses} funds={funds} tree={tree} classSpark={classSpark} />
        )}
        {tab === 'source' && (
          <BySourceTab sources={sources} funds={funds} tree={tree} total={summary?.balance} />
        )}
        {tab === 'perf' && (
          <PerformanceTab funds={funds} tree={tree} asOf={returnsAsOf} onPickFund={onPickFund} onPickModel={onPickModel} />
        )}
      </div>
    </Surface>
  );
}

HoldingsTabs.propTypes = {
  funds: PropTypes.array,
  /** Model-aware holdings list (FM-2110) — every view groups by it. */
  tree: PropTypes.array,
  /** Plan record; its `actions` decide whether elections (and drift) apply. */
  plan: PropTypes.object,
  summary: PropTypes.object,
  holdings: PropTypes.object,
  assetClasses: PropTypes.array,
  sources: PropTypes.array,
  classSpark: PropTypes.object,
  asOf: PropTypes.instanceOf(Date),
  onPickFund: PropTypes.func,
  /** Forwarded to By Fund / Performance: a model row opens the model allocation modal. */
  onPickModel: PropTypes.func,
  loading: PropTypes.bool,
  /** Optional card heading, shown above the tabs with the as-of date beside it. */
  title: PropTypes.node,
};

export default HoldingsTabs;
